import Joi from 'joi';
import { InvoiceItem, PriceItem } from 'types/invoice.types';
import { RevenueItemUnit } from 'types/revenues.types';
import { divideBy1000, excludeVAT, multiplyBy1000 } from 'utils/math';
import { formatErrorMessage } from 'validation/commonValidators';

export const priceItemValidationSchema = Joi.object({
  unitAmountExclVAT: Joi.number()
    .min(0)
    .required()
    .messages({
      ...formatErrorMessage('invoiceItem', 'unitAmountExclVAT'),
      'number.min': 'invoiceItem.unitAmountExclVAT.number.min_0',
    }),
  VATRate: Joi.when('category.admitsVAT', {
    is: true,
    then: Joi.number()
      .min(0)
      .required()
      .messages({ '*': 'invoiceItem.VATRate.any.required' }),
  }),
  name: Joi.string()
    .trim()
    .min(1)
    .max(900)
    .required()
    .messages(formatErrorMessage('invoiceItem', 'name')),
  description: Joi.string().trim().allow('', null).max(900).messages({
    'string.max': 'invoiceItem.description.string.max',
  }),
  categoryId: Joi.string()
    .required()
    .messages({ '*': 'invoiceItem.categoryId.any.required' }),
  unit: Joi.any().default(RevenueItemUnit.items),
});

export const getDefaultValue = (values?: Partial<PriceItem>) => {
  return {
    _id: values?._id || undefined,
    unitAmountExclVAT: values?.unitAmountExclVAT || 0,
    VATRate: values?.VATRate ?? undefined,
    _originalVATRate: values?.VATRate ?? undefined,
    name: values?.name || '',
    description: values?.description || '',
    categoryId: values?.categoryId || undefined,
    category: values?.category || undefined,
    unit: values?.unit || RevenueItemUnit.items,
  };
};

export const invoiceItemToPriceItem = (item: InvoiceItem) => {
  return {
    unitAmountExclVAT: !item.doesUnitPriceIncludeVAT
      ? item.unitAmountExclVAT
      : Math.round(
          multiplyBy1000(
            excludeVAT(
              divideBy1000(item.unitAmountInclVAT),
              divideBy1000(item.VATRate),
            ),
          ),
        ),
    VATRate: item.VATRate,
    name: item.name,
    description: item.description,
    categoryId: item.categoryId,
    unit: item.unit,
  };
};

const isInvoiceItemSufficient = (invoiceItem: Partial<InvoiceItem>) => {
  const {
    unitAmountExclVAT,
    unitAmountInclVAT,
    doesUnitPriceIncludeVAT,
    name,
    categoryId,
  } = invoiceItem;
  if (!name?.trim() || !categoryId) return false;
  if (doesUnitPriceIncludeVAT) {
    return unitAmountInclVAT != null;
  }
  return unitAmountExclVAT != null;
};

export const isPriceItemEqual = (
  priceItem: PriceItem,
  partialInvoiceItem: Partial<InvoiceItem>,
) => {
  if (!isInvoiceItemSufficient(partialInvoiceItem)) return false;

  const priceItem2 = invoiceItemToPriceItem(partialInvoiceItem as InvoiceItem);

  return (
    priceItem.unitAmountExclVAT === priceItem2.unitAmountExclVAT &&
    priceItem.VATRate === priceItem2.VATRate &&
    priceItem.name?.trim() === priceItem2.name?.trim() &&
    (priceItem.description?.trim() || '') ===
      (priceItem2.description?.trim() || '') &&
    priceItem.categoryId === priceItem2.categoryId &&
    (priceItem.unit || RevenueItemUnit.items) ===
      (priceItem2.unit || RevenueItemUnit.items)
  );
};
